import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import apiService from '../services/api';
import { TaskDetailResponse, TaskMetaResponse } from '../types/task';
import { NOTE_QUILL_MODULES } from '../utils/noteQuill';

type Props = {
  open: boolean;
  task?: TaskDetailResponse | null;
  accountId?: string;
  caseId?: string;
  supportDeskTicketId?: string;
  onClose: () => void;
  onSaved: () => void;
};

type AccountLinkOption = { id: string; display_name: string; sonic_account_number?: string | null };
type CaseLinkOption = { id: string; case_number: string; subject: string };
type TicketLinkOption = { id: string; ticket_number: string; subject?: string | null };
type LinkTarget = 'none' | 'account' | 'case' | 'ticket';

function formatAccountLinkLabel(a: AccountLinkOption) {
  const num = a.sonic_account_number?.trim();
  return num ? `${num} — ${a.display_name || 'Account'}` : a.display_name || a.id;
}

function formatCaseLabel(c: CaseLinkOption) {
  return `${c.case_number} — ${c.subject || 'Case'}`;
}

function formatTicketLabel(t: TicketLinkOption) {
  return `${t.ticket_number} — ${(t.subject || '').trim() || 'Ticket'}`;
}

const TaskFormDialogV2: React.FC<Props> = ({
  open,
  task,
  accountId,
  caseId,
  supportDeskTicketId,
  onClose,
  onSaved,
}) => {
  const isEdit = !!task?.id;
  const [meta, setMeta] = useState<TaskMetaResponse | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [statusCode, setStatusCode] = useState('new');
  const [typeCode, setTypeCode] = useState('general');
  const [dueAt, setDueAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [linkTarget, setLinkTarget] = useState<LinkTarget>('none');
  const [accountLink, setAccountLink] = useState<AccountLinkOption | null>(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountOptions, setAccountOptions] = useState<AccountLinkOption[]>([]);
  const [caseLink, setCaseLink] = useState<CaseLinkOption | null>(null);
  const [caseSearch, setCaseSearch] = useState('');
  const [caseOptions, setCaseOptions] = useState<CaseLinkOption[]>([]);
  const [ticketLink, setTicketLink] = useState<TicketLinkOption | null>(null);
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketOptions, setTicketOptions] = useState<TicketLinkOption[]>([]);

  const presetLocked = !!(accountId || caseId || supportDeskTicketId);

  useEffect(() => {
    if (!open) return;
    apiService
      .getTaskMeta()
      .then(setMeta)
      .catch(() => setMeta({ types: [], statuses: [] } as TaskMetaResponse));
  }, [open]);

  useEffect(() => {
    if (!open || isEdit) return;
    let cancelled = false;
    (async () => {
      if (accountId) {
        setLinkTarget('account');
        try {
          const acc = await apiService.getAccount(accountId);
          if (cancelled) return;
          setAccountLink({
            id: acc.id,
            display_name: acc.display_name || '',
            sonic_account_number: acc.sonic_account_number ?? null,
          });
          setAccountSearch(
            formatAccountLinkLabel({
              id: acc.id,
              display_name: acc.display_name || '',
              sonic_account_number: acc.sonic_account_number ?? null,
            })
          );
        } catch {
          if (!cancelled) {
            setAccountLink({ id: accountId, display_name: accountId, sonic_account_number: null });
            setAccountSearch(accountId);
          }
        }
        return;
      }
      if (caseId) {
        setLinkTarget('case');
        try {
          const c = await apiService.getCase(caseId);
          if (cancelled) return;
          setCaseLink({
            id: c.id,
            case_number: c.case_number || c.id,
            subject: c.subject || '',
          });
          setCaseSearch(formatCaseLabel({ id: c.id, case_number: c.case_number, subject: c.subject }));
        } catch {
          if (!cancelled) {
            setCaseLink({ id: caseId, case_number: caseId, subject: '' });
            setCaseSearch(caseId);
          }
        }
        return;
      }
      if (supportDeskTicketId) {
        setLinkTarget('ticket');
        try {
          const tk = await apiService.getSupportDeskTicket(supportDeskTicketId);
          if (cancelled) return;
          setTicketLink({
            id: tk.id,
            ticket_number: tk.ticket_number || tk.id,
            subject: tk.subject ?? null,
          });
          setTicketSearch(formatTicketLabel({ id: tk.id, ticket_number: tk.ticket_number, subject: tk.subject }));
        } catch {
          if (!cancelled) {
            setTicketLink({ id: supportDeskTicketId, ticket_number: supportDeskTicketId, subject: null });
            setTicketSearch(supportDeskTicketId);
          }
        }
        return;
      }
      setLinkTarget('none');
      setAccountLink(null);
      setAccountSearch('');
      setCaseLink(null);
      setCaseSearch('');
      setTicketLink(null);
      setTicketSearch('');
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isEdit, accountId, caseId, supportDeskTicketId]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setTitle(task?.title || '');
    setDescription(task?.description || '');
    setStatusCode((task?.status_code as string) || 'new');
    setTypeCode((task?.type_code as string) || 'general');
    setDueAt(task?.due_at ? new Date(task.due_at).toISOString().slice(0, 16) : '');
  }, [open, task]);

  useEffect(() => {
    const q = accountSearch.trim();
    if (q.length < 2 || linkTarget !== 'account' || presetLocked) {
      if (linkTarget !== 'account' || presetLocked) setAccountOptions([]);
      return;
    }
    const tmr = window.setTimeout(() => {
      apiService
        .getAccounts({ q, per_page: 25, page: 1 })
        .then((r: any) => {
          const rows = (r.accounts || []) as any[];
          setAccountOptions(
            rows.map((a) => ({
              id: a.id,
              display_name: a.display_name || '',
              sonic_account_number: a.sonic_account_number ?? null,
            }))
          );
        })
        .catch(() => setAccountOptions([]));
    }, 320);
    return () => window.clearTimeout(tmr);
  }, [accountSearch, linkTarget, presetLocked]);

  useEffect(() => {
    const q = caseSearch.trim();
    if (q.length < 2 || linkTarget !== 'case' || presetLocked) {
      if (linkTarget !== 'case' || presetLocked) setCaseOptions([]);
      return;
    }
    const tmr = window.setTimeout(() => {
      apiService
        .getCases({ q, per_page: 20, page: 1 })
        .then((r) => {
          setCaseOptions(
            (r.cases || []).map((c: any) => ({
              id: c.id,
              case_number: c.case_number || c.id,
              subject: c.subject || '',
            }))
          );
        })
        .catch(() => setCaseOptions([]));
    }, 320);
    return () => window.clearTimeout(tmr);
  }, [caseSearch, linkTarget, presetLocked]);

  useEffect(() => {
    const q = ticketSearch.trim();
    if (q.length < 2 || linkTarget !== 'ticket' || presetLocked) {
      if (linkTarget !== 'ticket' || presetLocked) setTicketOptions([]);
      return;
    }
    const tmr = window.setTimeout(() => {
      apiService
        .getSupportDeskTickets({ q, per_page: 20, page: 1 })
        .then((r) => {
          setTicketOptions(
            (r.tickets || []).map((t: any) => ({
              id: t.id,
              ticket_number: t.ticket_number || t.id,
              subject: t.subject ?? null,
            }))
          );
        })
        .catch(() => setTicketOptions([]));
    }, 320);
    return () => window.clearTimeout(tmr);
  }, [ticketSearch, linkTarget, presetLocked]);

  const canSave = useMemo(() => title.trim().length > 0, [title]);

  const linkPayload = () => {
    if (linkTarget === 'account' && accountLink) {
      return { account_id: accountLink.id, case_id: null, support_desk_ticket_id: null };
    }
    if (linkTarget === 'case' && caseLink) {
      return { case_id: caseLink.id, account_id: null, support_desk_ticket_id: null };
    }
    if (linkTarget === 'ticket' && ticketLink) {
      return { support_desk_ticket_id: ticketLink.id, account_id: null, case_id: null };
    }
    return { account_id: null, case_id: null, support_desk_ticket_id: null };
  };

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      if (isEdit && task?.id) {
        await apiService.updateTask(task.id, {
          title: title.trim(),
          description: description || null,
          status_code: statusCode,
          type_code: typeCode,
          due_at: dueAt ? new Date(dueAt).toISOString() : null,
        });
      } else {
        const links = presetLocked
          ? {
              account_id: accountId || null,
              case_id: caseId || null,
              support_desk_ticket_id: supportDeskTicketId || null,
            }
          : linkPayload();
        await apiService.createTask({
          title: title.trim(),
          description: description || null,
          status_code: statusCode,
          type_code: typeCode,
          due_at: dueAt ? new Date(dueAt).toISOString() : null,
          ...links,
        });
      }
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit Task' : 'New Task'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth autoFocus />
          <Typography variant="caption" color="text.secondary">
            Description
          </Typography>
          <ReactQuill theme="snow" value={description} onChange={setDescription} modules={NOTE_QUILL_MODULES} style={{ minHeight: 160 }} />
          {!isEdit && (
            <>
              <Typography variant="subtitle2" color="text.secondary">
                Link (optional)
              </Typography>
              {!presetLocked ? (
                <>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={linkTarget}
                    onChange={(_, v: LinkTarget | null) => v != null && setLinkTarget(v)}
                    sx={{ flexWrap: 'wrap' }}
                  >
                    <ToggleButton value="none">None</ToggleButton>
                    <ToggleButton value="account">Account</ToggleButton>
                    <ToggleButton value="case">Case</ToggleButton>
                    <ToggleButton value="ticket">Support ticket</ToggleButton>
                  </ToggleButtonGroup>
                  {linkTarget === 'account' && (
                    <Autocomplete
                      size="small"
                      options={accountOptions}
                      value={accountLink}
                      inputValue={accountSearch}
                      onInputChange={(_, v) => setAccountSearch(v)}
                      onChange={(_, newVal) => {
                        setAccountLink(newVal);
                        setAccountSearch(newVal ? formatAccountLinkLabel(newVal) : '');
                      }}
                      getOptionLabel={(o) => formatAccountLinkLabel(o)}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      filterOptions={(x) => x}
                      noOptionsText={accountSearch.trim().length < 2 ? 'Type at least 2 characters' : 'No accounts found'}
                      renderInput={(params) => <TextField {...params} label="Customer account" />}
                    />
                  )}
                  {linkTarget === 'case' && (
                    <Autocomplete
                      size="small"
                      options={caseOptions}
                      value={caseLink}
                      inputValue={caseSearch}
                      onInputChange={(_, v) => setCaseSearch(v)}
                      onChange={(_, newVal) => {
                        setCaseLink(newVal);
                        setCaseSearch(newVal ? formatCaseLabel(newVal) : '');
                      }}
                      getOptionLabel={(o) => formatCaseLabel(o)}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      filterOptions={(x) => x}
                      noOptionsText={caseSearch.trim().length < 2 ? 'Type at least 2 characters' : 'No cases found'}
                      renderInput={(params) => <TextField {...params} label="Case" />}
                    />
                  )}
                  {linkTarget === 'ticket' && (
                    <Autocomplete
                      size="small"
                      options={ticketOptions}
                      value={ticketLink}
                      inputValue={ticketSearch}
                      onInputChange={(_, v) => setTicketSearch(v)}
                      onChange={(_, newVal) => {
                        setTicketLink(newVal);
                        setTicketSearch(newVal ? formatTicketLabel(newVal) : '');
                      }}
                      getOptionLabel={(o) => formatTicketLabel(o)}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      filterOptions={(x) => x}
                      noOptionsText={ticketSearch.trim().length < 2 ? 'Type at least 2 characters' : 'No tickets found'}
                      renderInput={(params) => <TextField {...params} label="Support ticket" />}
                    />
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  This task will be created on the current {accountId ? 'account' : caseId ? 'case' : 'ticket'}.
                </Typography>
              )}
            </>
          )}
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={statusCode} onChange={(e) => setStatusCode(String(e.target.value))}>
                {(meta?.statuses || []).map((s) => (
                  <MenuItem key={s.code} value={s.code}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={typeCode} onChange={(e) => setTypeCode(String(e.target.value))}>
                {(meta?.types || []).map((t) => (
                  <MenuItem key={t.code} value={t.code}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            label="Due"
            type="datetime-local"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || !canSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskFormDialogV2;
